import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { openNavStyles } from '@/components/modules/app-navigation/open';
import { closedBadgeVariants, navItemVariants } from '@/components/modules/app-navigation/utils/transitions';
import { VisuallyHidden } from '@/components/visually-hidden';
import { AppNavItem } from '@/lib/constants';
import { tv } from '@/utils/styles';

export interface NavButtonProps {
  item: AppNavItem;
  onClick: () => void;
  badgeCount?: number;
  enableBadge?: boolean;
  visibility?: 'visible' | 'hidden';
  isOpen?: boolean;
}

const NavButton = ({ item, onClick, badgeCount, enableBadge, visibility = 'hidden', isOpen }: NavButtonProps) => {
  const pathname = usePathname() || '';
  const [tempCount, setTempCount] = useState(badgeCount);
  const Icon = item.icon;

  const isActive = pathname === item.url;

  // We set a temp value to handle the case where the badgeCount goes to 0. We
  // want the badge to fade away without flashing 0 as the count so we
  // temporarily store the count any time it isn't 0
  useEffect(() => {
    if (badgeCount !== 0) {
      setTempCount(badgeCount);
    }
  }, [badgeCount]);

  const { icon, trigger, triggerBg } = openNavStyles();
  const { badge, textLabel } = styles();

  return (
    <button type="button" onClick={onClick} className={trigger({ className: 'relative' })} title={item.label}>
      <Icon className={icon({ className: isActive ? 'text-white' : '' })} />
      {isOpen && (
        <motion.span className={textLabel()} variants={navItemVariants} animate={visibility} initial={visibility}>
          {item.label}
        </motion.span>
      )}
      {enableBadge && (
        <motion.span animate={badgeCount ? 'closed' : 'open'} variants={closedBadgeVariants} className={badge()}>
          {tempCount}
        </motion.span>
      )}
      <VisuallyHidden>{item.label}</VisuallyHidden>
      <span
        className={triggerBg({
          isActive,
        })}
      />
    </button>
  );
};

const styles = tv({
  slots: {
    badge: 'absolute left-5 top-1 z-10 rounded-full bg-error px-1 py-0.5 text-[8px]',
    textLabel: 'z-2 ml-2.5 text-white',
  },
});

export { NavButton };
